/**
 * Datos estáticos de la landing page.
 * Centralizados aquí para evitar duplicación y facilitar mantenimiento del copy.
 */
import {
  ShieldCheck,
  ScanSearch,
  FileCheck2,
  Package,
  AlertTriangle,
  Scale,
  Clock,
  Wallet,
  Sparkles,
  Eye,
} from "lucide-react";

export const tiers = [
  {
    key: "n0",
    title: "Prevalidación",
    subtitle: "Explorador gratuito",
    price: 0,
    icon: ScanSearch,
    badge: null,
    description: "Evalúa si tu caso está listo para estructurarse.",
    features: [
      "Reporte de prevalidación del Precontrato",
      "Semáforo de completitud",
      "Alertas iniciales",
      "Recomendación para el siguiente paso",
    ],
    cta: "Prevalidar gratis",
    highlight: false,
    accent: false,
    intent: "n0",
  },
  {
    key: "n1_auto",
    title: "Diagnóstico",
    subtitle: "Diagnóstico automatizado",
    price: 199000,
    icon: Sparkles,
    badge: { label: "Sin revisión humana", color: "amber" },
    description: "Recibe ruta recomendada, alertas, riesgos preliminares y checklist.",
    features: [
      "Informe de diagnóstico (PDF + Word)",
      "Ruta o modalidad recomendada",
      "Matriz preliminar de riesgos",
      "Alertas jurídicas y operativas",
    ],
    cta: "Comprar Diagnóstico",
    highlight: false,
    accent: false,
    intent: "n1_auto",
  },
  {
    key: "n1_reviewed",
    title: "Diagnóstico Revisado",
    subtitle: "Diagnóstico con revisión humana",
    price: 489000,
    icon: FileCheck2,
    badge: { label: "Con revisión humana experta", color: "emerald" },
    description: "Diagnóstico automático + revisión humana de coherencia y riesgos.",
    features: [
      "Todo lo del Diagnóstico automatizado",
      "Informe revisado por experto",
      "Hoja de observaciones",
      "Versión ajustada del diagnóstico",
      "Checklist de revisión humana",
    ],
    cta: "Comprar Diagnóstico Revisado",
    highlight: true,
    accent: true,
    intent: "n1_reviewed",
  },
  {
    key: "n2_auto",
    title: "Kit Documental",
    subtitle: "Kit Documental automatizado",
    price: 799000,
    icon: Package,
    badge: { label: "Sin revisión humana", color: "amber" },
    description:
      "Genera estudios previos, análisis básico, matriz de riesgos, comparables, checklist y borradores.",
    features: [
      "Estudios previos / documento base",
      "Análisis del sector / mercado básico",
      "Matriz de riesgos y comparables",
      "Borrador de pliego y minuta contractual",
    ],
    cta: "Comprar Kit Documental",
    highlight: false,
    accent: false,
    intent: "n2_auto",
  },
  {
    key: "n2_reviewed",
    title: "Kit Documental Revisado",
    subtitle: "Kit Documental con revisión humana",
    price: 1599000,
    icon: ShieldCheck,
    badge: { label: "Con revisión humana experta", color: "emerald" },
    description: "Kit automático + revisión experta, observaciones y versión ajustada.",
    features: [
      "Todo lo del Kit Documental automatizado",
      "Kit revisado por experto",
      "Informe de revisión del Kit",
      "Control de cambios / comentarios",
      "Versión final asistida",
    ],
    cta: "Comprar Kit Revisado",
    highlight: false,
    accent: false,
    intent: "n2_reviewed",
  },
];

export const problemPoints = [
  { icon: AlertTriangle, text: "Procesos anulados o suspendidos por modalidad de selección incorrecta." },
  { icon: Scale, text: "Sanciones fiscales y disciplinarias por estudios previos débiles." },
  { icon: Clock, text: "Demoras por requerimientos del órgano de control sobre justificaciones ambiguas." },
  { icon: Wallet, text: "Asesoría legal especializada de costo elevado y agendas saturadas." },
];

export const audiences = [
  {
    iconName: "juzgados",
    title: "Entidades públicas",
    body: "Evita anulaciones, sanciones y demoras. Estructura procesos con trazabilidad legal completa antes de publicar.",
  },
  {
    iconName: "abogado",
    title: "Asesores y firmas",
    body: "Acelera diagnósticos precontractuales para tus clientes. Reduce horas en revisión técnica básica.",
  },
  {
    iconName: "ciudadano",
    title: "Comité asesor",
    body: "Insumo de apoyo para discutir riesgos, modalidad y completitud antes del comité de contratación.",
  },
];

export const steps = [
  { n: "01", icon: ScanSearch, title: "Captura tu caso", body: "Registra objeto contractual, valor estimado, modalidad propuesta y plazo." },
  { n: "02", icon: Eye, title: "Recibe prevalidación gratuita", body: "Semáforo del proceso y hallazgos preliminares en minutos." },
  { n: "03", icon: FileCheck2, title: "Solicita diagnóstico", body: "Diagnóstico precontractual automatizado o con revisión humana experta." },
  { n: "04", icon: Package, title: "Avanza al Kit Documental", body: "Estudios previos, sector, matriz de riesgos y borradores del proceso." },
];

export const whyPoints = [
  {
    iconName: "normas",
    title: "Lista blanca normativa",
    body: "La IA solo cita normas colombianas vigentes verificadas: 35 referencias entre Constitución, leyes y decretos.",
  },
  {
    iconName: "abogado",
    title: "Revisión humana experta",
    body: "Ningún Diagnóstico o Kit Documental Revisado se descarga sin aprobación previa de un revisor experto.",
  },
  {
    iconName: "fuentes-normativas",
    title: "Comparables SECOP",
    body: "Cuando se habilite, tus decisiones se contrastan con datos reales de procesos similares publicados en SECOP.",
  },
  {
    iconName: "jurisprudencia",
    title: "Trazabilidad legal",
    body: "Cada diagnóstico queda auditado, versionado y firmado para soportar la decisión de la entidad.",
  },
];

export const normChips = [
  "Constitución Política",
  "Ley 80 de 1993",
  "Ley 1150 de 2007",
  "Ley 1474 de 2011",
  "Decreto 1082 de 2015",
  "Ley 2195 de 2022",
  "Ley 1581 de 2012",
];

export const faqs = [
  {
    q: "¿JustSapiens Procure reemplaza al abogado o al ordenador del gasto?",
    a: "No. Es un insumo de apoyo basado en IA con revisión humana experta. La responsabilidad disciplinaria, fiscal y penal sobre el proceso permanece en cabeza del ordenador del gasto, comité asesor y comité de contratación de la entidad.",
  },
  {
    q: "¿Mis datos están protegidos?",
    a: "Sí. Tratamos la información conforme a la Ley 1581 de 2012 y nuestra Política de Tratamiento de Datos. Tus casos son privados y solo el equipo autorizado los visualiza para revisión humana.",
  },
  {
    q: "¿Cuánto demora cada entrega?",
    a: "La Prevalidación es inmediata. Las variantes automatizadas se entregan en minutos. Las variantes con revisión humana experta se entregan en 24 a 72 horas hábiles según el caso.",
  },
  {
    q: "¿Puedo descargar el documento sin revisión humana?",
    a: "Solo en las variantes automatizadas, y con un aviso legal expreso de que no hay revisión humana. Las variantes Revisadas requieren aprobación de un revisor experto antes de habilitar la descarga.",
  },
  {
    q: "¿Cómo se pagan los niveles?",
    a: "Pago único por caso vía MercadoPago (tarjeta, PSE, Nequi, Bre-B). La Prevalidación es gratuita sin tarjeta. Algunos usuarios cuentan con cuota gratuita mensual de Diagnósticos y Kits según su plan.",
  },
];
